import { createEntityAdapter, EntityState, Update } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { OrderType } from 'graphql-api/baskets-api-interfaces';

import { manualDiscountsUpdatedFailed } from '../actions';
import {
  applyBasketManualDiscountsValidated,
  clearBasketManualDiscounts,
  loadBasketManualDiscounts,
  loadMaxDiscountPercentageForMaterialsSuccess,
  removeBasketManualDiscounts,
} from '../actions/basket-manual-discounts.actions';

export const basketManualDiscountsFeatureKey = 'manual-discounts';

export type ManualDiscountEntity = {
  basketId: string;
  basketItemId: string;
  currentValue: number | null;
  previousValue?: number | null;
};

export interface State extends EntityState<ManualDiscountEntity> {
  maxDiscountPercentagesForProducts: Partial<{
    [orderType in OrderType]: { [productId: string]: number };
  }>;
}

export const selectId = (entity: ManualDiscountEntity) => entity.basketItemId;

export const adapter = createEntityAdapter({
  selectId,
});

export const initialState: State = adapter.getInitialState({
  maxDiscountPercentagesForProducts: {},
});

export const reducer = createReducer(
  initialState,
  on(applyBasketManualDiscountsValidated, (state, validatedAction) => {
    const action = validatedAction.applyBasketManualDiscountsAction;
    const entities = Object.entries(action.values).map(
      ([basketItemId, discount]) => {
        const entity: ManualDiscountEntity = {
          basketId: action.basketId,
          basketItemId,
          currentValue: discount,
          previousValue: state.entities[basketItemId]?.currentValue,
        };

        return entity;
      },
    );

    return adapter.upsertMany(entities, state);
  }),
  on(loadBasketManualDiscounts, (state, { basketId, entities }) => {
    const newState = removeManualDiscountEntitiesForBasketId(state, basketId);
    return adapter.upsertMany(entities, newState);
  }),
  on(removeBasketManualDiscounts, (state, action) => {
    return adapter.removeMany(action.basketItemIds, state);
  }),
  on(clearBasketManualDiscounts, (state, action) => {
    const clearAll = !action.basketItemIds;
    const updates: Update<ManualDiscountEntity>[] = (
      Object.values(state.entities) as ManualDiscountEntity[]
    )
      .filter(
        (e) =>
          e.basketId === action.basketId &&
          (clearAll || action.basketItemIds?.includes(e.basketItemId)),
      )
      .map((entity) => {
        const newEntity: Update<ManualDiscountEntity> = {
          id: entity.basketItemId,
          changes: {
            currentValue: null,
            previousValue: undefined,
          },
        };

        return newEntity;
      });

    return adapter.updateMany(updates, state);
  }),
  on(manualDiscountsUpdatedFailed, (state, action) => {
    const entities = Object.values(state.entities)
      .filter((e) => !!e && e.basketId === action.basketId)
      .map((e) => {
        const entity = e as ManualDiscountEntity;
        return {
          ...entity,
          currentValue: entity.previousValue ?? 0,
          previousValue: undefined,
        };
      });

    return adapter.upsertMany(entities, state);
  }),
  on(loadMaxDiscountPercentageForMaterialsSuccess, (state, action) => {
    return {
      ...state,
      maxDiscountPercentagesForProducts: {
        ...action.discountPercentages,
      },
    };
  }),
);

function removeManualDiscountEntitiesForBasketId(
  state: State,
  basketId: string,
): State {
  const basketItemIdsToRemove = (
    Object.values(state.entities) as ManualDiscountEntity[]
  )
    .filter((e) => e.basketId === basketId)
    .map((e) => e.basketItemId);

  return adapter.removeMany(basketItemIdsToRemove, state);
}
